import React, { useState } from 'react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import ComplianceSummary from '@/pages/ClimateProDashboard/DataCompliance/ComplianceSummary/ComplianceSummary.tsx';
import MissingDataSection from '@/pages/ClimateProDashboard/DataCompliance/MissingDataSection/MissingDataSection.tsx';
import styles from './PastTrips.module.scss';
import breakpoints from '@/constants/breakpoints.ts';
import SubmenuItems from '@/components/SubmenuItems/SubmenuItems.tsx';
import { useWindowSize } from '@react-hook/window-size';
import routes from '@/constants/routes.ts';
import Co2Period = App.Enums.Co2Period;
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { getDataComplianceSummary, getUncompliantTripsNum } from '@/api/dataCompliance.ts';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import AirplaneIcon from '@/assets/Icons/Airplane.svg?react';
import ArrowRightIcon from '@/assets/Icons/Right-Arrow-Extended.svg?react';
import { useModalStore } from '@/store/useModalStore.ts';
import Modal from '@/components/ui/Modal/Modal.tsx';
import ChecklistIcon from '@/assets/Icons/Checklist.svg?react';
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';
import {
	actionNeededDefaultPeriod,
	actionNeededReportingPeriod,
	dataCompliancePeriodLabels,
} from '@/constants/dataCompliance.ts';
import Skeleton from 'react-loading-skeleton';

export const submenuLinks = [
	{ path: routes.dataCompliancePastTrips, label: 'Trip data coverage' },
	{
		path: routes.dataComplianceSettings,
		label: 'Settings',
	},
];

const dataComplianceLearnMoreModal = 'dataComplianceLearnMoreModal';

const PastTrips: React.FC = () => {
	const [width] = useWindowSize();
	const navigate = useNavigate();
	const { openModal, closeModal } = useModalStore();
	const [timePeriod, setTimePeriod] = useState<Co2Period>('last-30-days');
	const dataComplianceQuery = useQuery({
		queryKey: [queryKeys.dataComplianceSummary, timePeriod],
		queryFn: async () => {
			return await getDataComplianceSummary({
				period: timePeriod,
			});
		},
	});
	const last30DaysAssignedTripsQuery = useQuery({
		queryKey: [queryKeys.dataComplianceNumOfTrips, actionNeededDefaultPeriod],
		queryFn: () => getUncompliantTripsNum({ period: actionNeededDefaultPeriod }),
	});
	const reportingPeriodAssignedTripsQuery = useQuery({
		queryKey: [queryKeys.dataComplianceNumOfTrips, actionNeededReportingPeriod],
		queryFn: () => getUncompliantTripsNum({ period: actionNeededReportingPeriod }),
		enabled: last30DaysAssignedTripsQuery.data?.count === 0,
	});
	const last30DaysAssignedTripsCount = last30DaysAssignedTripsQuery.data?.count ?? 0;
	const reportingPeriodAssignedTripsCount = reportingPeriodAssignedTripsQuery.data?.count ?? 0;
	const last30DaysPeriodLabel = dataCompliancePeriodLabels[actionNeededDefaultPeriod].toLocaleLowerCase();
	const shouldShowReportingPeriodNote =
		last30DaysAssignedTripsCount === 0 &&
		reportingPeriodAssignedTripsQuery.isSuccess &&
		reportingPeriodAssignedTripsCount > 0;
	const shouldHideAssignedTripsCard =
		last30DaysAssignedTripsQuery.isSuccess &&
		last30DaysAssignedTripsCount === 0 &&
		reportingPeriodAssignedTripsQuery.isSuccess &&
		reportingPeriodAssignedTripsCount === 0;

	return (
		<>
			{width < breakpoints.tabletWide && <SubmenuItems links={submenuLinks} />}
			<FlexBlock flexDirection="column" className={styles.pastTrips}>
				<FlexBlock justifyContent="space-between">
					<h1>CO₂ data gap</h1>
					<div className={styles.infoButton}>
						<Tooltip
							placement="right"
							showLearnMoreText
							learnMoreLabel="How this works"
							onLearnMoreClick={() => {
								trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.help, {
									postHogEventName: 'data_gap_how_this_works_clicked',
									postHogProperties: { category: 'data-gap' },
								});
								openModal(dataComplianceLearnMoreModal);
							}}
							text="Learn how trip data coverage works for completed trips with missing accommodation or transport details."
						/>
					</div>
				</FlexBlock>
				<p className={styles.additionalInfo}>
					Review completed trips with missing accommodation or transport details that may affect your
					organization's emissions reporting.
				</p>
				{!shouldHideAssignedTripsCard && (
					<FlexBlock
						className={styles.dataQualityCards}
						flexDirection={{ [breakpoints.zero]: 'column', [breakpoints.tabletSmall]: 'row' }}
					>
						<div
							className={styles.assignedTripsCard}
							aria-label="Trips you can review that need reporting details"
							aria-busy={last30DaysAssignedTripsQuery.isLoading}
						>
							<span className={styles.assignedIconWrapper} aria-hidden="true">
								<AirplaneIcon />
							</span>
							<div className={styles.assignedTripsInfo}>
								<div className={styles.assignedTripsHeader}>
									<h2>You have trips that might be missing details</h2>
								</div>
								<div className={styles.assignedTripsCountRow}>
									{last30DaysAssignedTripsQuery.isLoading ? (
										<Skeleton width={280} height={24} />
									) : last30DaysAssignedTripsQuery.isError ? (
										<p>We couldn’t load your trip count.</p>
									) : last30DaysAssignedTripsCount === 0 ? (
										<>
											<p
												className={
													shouldShowReportingPeriodNote
														? styles.assignedTripsMainTextWithNote
														: undefined
												}
											>
												No trips to review from the {last30DaysPeriodLabel}.
											</p>
											{shouldShowReportingPeriodNote && (
												<p>
													You do have {reportingPeriodAssignedTripsCount}{' '}
													{reportingPeriodAssignedTripsCount === 1 ? 'trip' : 'trips'} in your
													company’s annual reporting period that{' '}
													{reportingPeriodAssignedTripsCount === 1 ? 'needs' : 'need'} more
													details.
												</p>
											)}
										</>
									) : (
										<p>
											You have{' '}
											<span className={styles.assignedTripsCount}>
												{last30DaysAssignedTripsCount}
											</span>{' '}
											{last30DaysAssignedTripsCount === 1 ? 'trip' : 'trips'} in the{' '}
											{last30DaysPeriodLabel} that{' '}
											{last30DaysAssignedTripsCount === 1 ? 'needs' : 'need'} more details
										</p>
									)}
								</div>
							</div>
							<button
								type="button"
								onClick={() => {
									trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
										deduplicationKey: 'review-trips',
										postHogEventName: 'data_gap_review_trips_clicked',
										postHogProperties: { category: 'data-gap' },
									});
									navigate(
										`${routes.tripEmissionLog}?mode=missing-details&period=${actionNeededDefaultPeriod}`,
									);
								}}
							>
								Review trips
							</button>
						</div>
					</FlexBlock>
				)}
				<ComplianceSummary
					timePeriod={timePeriod}
					setTimePeriod={setTimePeriod}
					dataComplianceQuery={dataComplianceQuery}
				/>
				<MissingDataSection timePeriod={timePeriod} />
				<Modal name={dataComplianceLearnMoreModal} className={styles.dataComplianceLearnMoreModal}>
					<h2 className={styles.modalTitle}>
						<span className={styles.modalTitleIcon} aria-hidden="true">
							<ChecklistIcon />
						</span>
						How CO₂ data gap works
					</h2>
					<p>
						A trip has the data needed for CO₂ reporting when it includes accommodation and transport
						information.
					</p>
					<p>
						This page gives company administrators an overview of trips that have been taken but are still
						missing this information. You can use it to see where details are missing and which travel
						arrangers are connected to those trips.
					</p>
					<p>
						Missing details can be added by the traveler or a travel arranger associated with the trip. If
						you have trips you can update yourself, you'll see them under "Your trips."
					</p>
					<p>
						When the missing information is added, the trip is included as complete in the completeness
						results.
					</p>
					<p>
						Travel arrangers can receive reminders about trips they booked. You can manage reminders and
						exclusions in Settings. Excluding someone from reminders does not remove their trips from the
						completeness results.
					</p>
					<Link to={routes.dataComplianceSettings} onClick={() => closeModal()}>
						<ArrowRightIcon aria-hidden="true" />
						Manage reminder settings
					</Link>
				</Modal>
			</FlexBlock>
		</>
	);
};

export default PastTrips;
